/**
 * Vessel Data Collector
 * Collects all vessel data into a single structured object
 */

/**
 * Collects all vessel data including basic info and bay configurations
 * @param {Object} params - Parameters containing all vessel data
 * @returns {Object} - Structured vessel data object
 */
export const collectVesselData = ({
  accommodationBay,
  bayConfigurations,
  deckMaxRange,
  deckMinRange,
  engineBay,
  holdMaxRange,
  holdMinRange,
  lowestDeckBay,
  mergeBays,
  operationType,
  rowMaxRange,
  rowMinRange,
  selectedRecord,
  twentiesConfigurations,
}) => {
  // Build tier/row range arrays from individual max/min inputs
  const toNum = v =>
    v !== '' && v !== null && v !== undefined ? Number(v) : null

  const deckTierLabelsRange =
    toNum(deckMaxRange) !== null && toNum(deckMinRange) !== null
      ? [toNum(deckMaxRange), toNum(deckMinRange)]
      : selectedRecord?.deckTierLabelsRange?.filter(
          v => v !== null && v !== undefined
        ).length > 0
      ? selectedRecord.deckTierLabelsRange.filter(
          v => v !== null && v !== undefined
        )
      : [1, 2]

  const holdTierLabelsRange =
    toNum(holdMaxRange) !== null && toNum(holdMinRange) !== null
      ? [toNum(holdMaxRange), toNum(holdMinRange)]
      : selectedRecord?.holdTierLabelsRange?.filter(
          v => v !== null && v !== undefined
        ).length > 0
      ? selectedRecord.holdTierLabelsRange.filter(
          v => v !== null && v !== undefined
        )
      : [1, 2]

  const rowLabelsRange =
    toNum(rowMaxRange) !== null && toNum(rowMinRange) !== null
      ? [toNum(rowMaxRange), toNum(rowMinRange)]
      : selectedRecord?.rowLabelsRange?.filter(
          v => v !== null && v !== undefined
        ).length > 0
      ? selectedRecord.rowLabelsRange.filter(v => v !== null && v !== undefined)
      : [1, 2]

  const vesselData = {
    // Basic vessel information
    abbreviation:
      selectedRecord?.abbreviation || selectedRecord?.vesselAbbreviation || '',
    active: selectedRecord?.active ?? true,
    id: selectedRecord?.id, // Include vessel ID for UPDATE operations
    imoNumber: Number(selectedRecord?.imoNumber || 0),
    vesselAbbreviation:
      selectedRecord?.vesselAbbreviation || selectedRecord?.abbreviation || '',
    vesselName: selectedRecord?.vesselName || '',

    // Bay configurations in API format (flat structure)
    // Only include accommodationAfterBay for POST operations (CREATE and CLONE)
    ...((operationType === 'CREATE' || operationType === 'CLONE') &&
    accommodationBay
      ? { accommodationAfterBay: Number(accommodationBay) }
      : {}),
    accommodationAfterBay: accommodationBay
      ? Number(accommodationBay)
      : undefined,
    engineAfterBay: engineBay ? Number(engineBay) : undefined,
    forties: bayConfigurations.map(config => ({
      bay: Number(config.bay),
      fortyFootOnlyBay: config.fourtyFootOnly || false,
      hatchCover: Number(config.hatchCovers),
    })),
    lowestDeckTier: lowestDeckBay ? Number(lowestDeckBay) : undefined,
    mergeBays: mergeBays || false,
    twenties: twentiesConfigurations.map(config => ({
      bay: Number(config.bay),
      fortyFootOnlyBay: false, // Required by API even for twenties
      hatchCover: Number(config.hatchCovers),
    })),

    // Tier and row label ranges - always provide arrays
    deckTierLabelsRange,
    holdTierLabelsRange,
    rowLabelsRange,
  }

  return vesselData
}

/**
 * Extract bay configurations from vessel record
 * @param {Object} record - Vessel record containing bay configuration data
 * @returns {Object} - Extracted bay configurations
 */
export const extractBayConfigurations = record => {
  // If no record, return default empty structure
  if (!record) {
    return {
      accommodationBay: '',
      bayConfigurations: [],
      engineBay: '',
      lowestDeckBay: '',
      mergeBays: false,
      twentiesConfigurations: [],
    }
  }

  // Support both nested bayConfiguration object and flat structure from API
  const bayConfig = record.bayConfiguration || record

  // Handle forties - support both API format and internal format
  const fortiesData = bayConfig.forties || []
  const bayConfigurations = fortiesData.map((config, index) => ({
    bay: String(config.bay ?? ''),
    fourtyFootOnly: config.fourtyFootOnly || config.fortyFootOnlyBay || false,
    hatchCovers: String(config.hatchCovers ?? config.hatchCover ?? ''),
    key: index,
  }))

  // Handle twenties - support both API format and internal format
  const twentiesData = bayConfig.twenties || []
  const twentiesConfigurations = twentiesData.map((config, index) => ({
    bay: String(config.bay ?? ''),
    hatchCovers: String(config.hatchCovers ?? config.hatchCover ?? ''),
    key: index,
  }))

  // Handle accommodation bay - support both spellings (accomodation vs accommodation)
  const accommodationBay = String(
    bayConfig.accommodationBay ??
      bayConfig.accommodationAfterBay ??
      record.accommodationAfterBay ??
      ''
  )

  // Handle engine bay
  const engineBay = String(
    bayConfig.engineBay ??
      bayConfig.engineAfterBay ??
      record.engineAfterBay ??
      ''
  )

  // Handle merge bays
  const mergeBays = bayConfig.mergeBays ?? record.mergeBays ?? false

  // Extract tier/row label ranges and split into individual max/min strings
  const deckArr = record.deckTierLabelsRange || []
  const holdArr = record.holdTierLabelsRange || []
  const rowArr = record.rowLabelsRange || []

  return {
    accommodationBay,
    bayConfigurations,
    deckMaxRange: String(deckArr?.[0] ?? ''),
    deckMinRange: String(deckArr?.[1] ?? ''),
    engineBay,
    holdMaxRange: String(holdArr?.[0] ?? ''),
    holdMinRange: String(holdArr?.[1] ?? ''),
    // lowestDeckBay,
    mergeBays,
    rowMaxRange: String(rowArr?.[0] ?? ''),
    rowMinRange: String(rowArr?.[1] ?? ''),
    twentiesConfigurations,
  }
}

export default collectVesselData
